import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  {
    label: "Services",
    href: "/services",
    children: [
      { label: "All Services", href: "/services" },
      { label: "Plumbing Services", href: "/plumbing" },
      { label: "Water Heater Repair", href: "/services#water-heaters" },
      { label: "Grease Trap Services", href: "/services#grease-traps" },
      { label: "Welding Services", href: "/services#welding" },
    ],
  },
  { label: "Service Areas", href: "/location" },
  { label: "Contact Us", href: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const isTransparent = !scrolled && !mobileOpen;

  return (
    <>
      {/* Emergency top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#E8500A] text-white text-xs py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 font-semibold">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
              24/7 Emergency Service Available
            </span>
          </div>
          <div className="flex items-center gap-5">
            <a href="tel:8178324968" className="flex items-center gap-1.5 font-bold hover:text-orange-100 transition-colors whitespace-nowrap">
              <i className="ri-phone-fill text-xs" />
              Arlington: (817) 832-4968
            </a>
            <span className="text-orange-200/50">|</span>
            <a href="tel:9722625113" className="flex items-center gap-1.5 font-bold hover:text-orange-100 transition-colors whitespace-nowrap">
              <i className="ri-phone-fill text-xs" />
              Dallas: (972) 262-5113
            </a>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <header
        className={`fixed top-[32px] left-0 right-0 z-40 transition-all duration-300 ${
          isTransparent
            ? "bg-transparent"
            : "bg-white/98 shadow-sm border-b border-gray-100"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-[68px]">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img
                src="https://jerrysplumbingheating.com/wp-content/uploads/2022/04/jerrys-plumbing-welding-dfw-logo.png"
                alt="Jerry's Plumbing Heating & Welding — DFW Master Plumber"
                className={`h-11 w-auto object-contain transition-all duration-300 ${
                  isTransparent ? "brightness-0 invert" : "brightness-100"
                }`}
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-7">
              {NAV_LINKS.map((link) =>
                link.children ? (
                  <div
                    key={link.label}
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <button
                      className={`flex items-center gap-1 text-sm font-semibold tracking-wide transition-colors cursor-pointer whitespace-nowrap ${
                        isTransparent
                          ? "text-white/90 hover:text-white"
                          : "text-gray-700 hover:text-[#E8500A]"
                      }`}
                    >
                      {link.label}
                      <i className={`ri-arrow-down-s-line text-xs transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                    </button>
                    {servicesOpen && (
                      <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-xl py-2 border border-gray-100 overflow-hidden">
                        {link.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 hover:text-[#E8500A] transition-colors"
                          >
                            <i className="ri-arrow-right-s-line text-orange-400 text-xs" />
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.label}
                    to={link.href}
                    className={`text-sm font-semibold tracking-wide transition-colors whitespace-nowrap ${
                      isTransparent
                        ? "text-white/90 hover:text-white"
                        : "text-gray-700 hover:text-[#E8500A]"
                    } ${
                      location.pathname === link.href
                        ? isTransparent
                          ? "text-white border-b-2 border-orange-400 pb-0.5"
                          : "text-[#E8500A] border-b-2 border-[#E8500A] pb-0.5"
                        : ""
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/contact"
                className={`text-sm font-semibold px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                  isTransparent
                    ? "text-white/90 hover:text-white border border-white/30 hover:border-white"
                    : "text-gray-700 hover:text-[#E8500A] border border-gray-200 hover:border-orange-300"
                }`}
              >
                Free Estimate
              </Link>
              <a
                href="tel:8178324968"
                className="flex items-center gap-2 bg-[#E8500A] hover:bg-[#d14508] text-white px-5 py-2.5 rounded-lg font-bold text-sm transition-colors whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill text-xs" />
                </span>
                Call Now
              </a>
            </div>

            {/* Mobile Toggle */}
            <button
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                isTransparent ? "text-white" : "text-gray-700"
              }`}
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle navigation menu"
            >
              <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-3-line"}`} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100">
            <div className="px-6 py-5 flex flex-col gap-1">
              {NAV_LINKS.map((link) =>
                link.children ? (
                  <div key={link.label}>
                    <button
                      className="w-full flex items-center justify-between py-3 text-gray-800 font-semibold text-sm cursor-pointer"
                      onClick={() => setServicesOpen((v) => !v)}
                    >
                      {link.label}
                      <i className={`ri-arrow-${servicesOpen ? "up" : "down"}-s-line text-gray-500`} />
                    </button>
                    {servicesOpen && (
                      <div className="pl-4 mb-2">
                        {link.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            className="flex items-center gap-2 py-2.5 text-sm text-gray-600 hover:text-[#E8500A]"
                          >
                            <i className="ri-arrow-right-s-line text-orange-400 text-xs" />
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.label}
                    to={link.href}
                    className={`block py-3 font-semibold text-sm border-b border-gray-50 ${
                      location.pathname === link.href ? "text-[#E8500A]" : "text-gray-800 hover:text-[#E8500A]"
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              )}
              <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col gap-3">
                <a
                  href="tel:8178324968"
                  className="flex items-center justify-center gap-2 bg-[#E8500A] text-white px-5 py-3.5 rounded-lg font-bold text-sm whitespace-nowrap"
                >
                  <i className="ri-phone-fill" />
                  Call (817) 832-4968 — Emergency Line
                </a>
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2 border border-gray-200 text-gray-700 px-5 py-3 rounded-lg font-semibold text-sm whitespace-nowrap"
                >
                  Request Free Estimate
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
